import React, { useEffect, useState } from "react";
import { ProjectRecord } from "@/types";
import { FormProvider, useForm } from "react-hook-form";
import Modal from "@/components/Modal";
import { StyledIconButton, StyledRightActionButton } from "@/components/styled";
import SvgUserEdit from "@/assets/icons/SVGUserEdit.tsx";
import ProjectForm from "@/pages/ProjectDetailsPage/components/ProjectForm.tsx";
import { AddProjectForm } from "@/pages/ProjectDetailsPage/components/AddProjectModal.tsx";
import { updateProject } from "@/api/projectsApi";
import { PROJECT_ALREADY_EXISTS_ERROR_MESSAGE } from "@/constants";
import SVGPlus from "@/assets/icons/SVGPlus.tsx";
import { useMediaQuery, useTheme } from "@mui/material";
import { validateCustomerName } from "@/utils/validateCustomerName.ts";
import { useErrorBoundary } from "@/libs/ErrorBoundary.tsx";
import { getBatteryModelReq } from "@/api/batteryApi";

type Props = {
  project: ProjectRecord;
  iconOnly?: boolean;
  getUpdatedProjects: () => void;
  getFilesReq?: () => void;
};

const EditProjectModal = ({
  project,
  getUpdatedProjects,
  getFilesReq,
  iconOnly = false,
}: Props) => {
  const handleError = useErrorBoundary();
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [step, setStep] = useState<1 | 2>(1);

  const methods = useForm<Partial<AddProjectForm>>({
    mode: "onChange",
  });
  const {
    handleSubmit,
    reset,
    control,
    watch,
    getValues,
    setValue,
    setError,
    formState: { isValid },
  } = methods;

  const batteryModel = project.batteryModel;
  const handleStep = async (step: 1 | 2) => {
    if (step === 1) {
      setStep(step);
      return;
    }

    const customerName = methods.getValues("customerName");
    if (step === 2 && customerName !== project?.customerName) {
      const customerNameError = await validateCustomerName(customerName ?? "");

      if (customerNameError !== true) {
        methods.setError("customerName", { message: customerNameError });
        return;
      }
    }

    setStep(step);
  };

  const mapProjectToFormData = (project: ProjectRecord): AddProjectForm & { extendToDate?: string; extendToDateNote?: string } => {
    const data = {
      ...project,
      upfrontIncentivePayment: `${Number(project.upfrontIncentivePayment) / 100}`,
    } as AddProjectForm & { extendToDate?: string; extendToDateNote?: string };

    const projectExtensions = project?.ProjectExtensions ?? [];

    if (projectExtensions.length > 0) {
      data.extendToDate = projectExtensions[projectExtensions.length - 1].extendToDate;
      data.extendToDateNote = projectExtensions[projectExtensions.length - 1].note;
    }

    return data;
  };

  const handleClose = () => {
    setOpen(false);

    const data = mapProjectToFormData(project)

    reset(data);
  };

  const handleFormChange = () => {
    const formData = getValues();
    const hasAnyChanges = Object.keys(formData).some(
      (key) =>
        formData[key as keyof AddProjectForm] !==
        project?.[key as keyof ProjectRecord],
    );
    setHasChanges(hasAnyChanges);
  };

  const getChangedValues = (
    formData: Partial<AddProjectForm>,
    initialData: ProjectRecord,
  ) => {
    const changedValues: Partial<AddProjectForm> = {};
    Object.keys(formData).forEach((key) => {
      if (
        formData[key as keyof AddProjectForm] !==
        initialData[key as keyof ProjectRecord]
      ) {
        changedValues[key as keyof AddProjectForm] =
          formData[key as keyof AddProjectForm];
      }
    });
    return changedValues;
  };

  const onSubmit = async (data: Partial<AddProjectForm>) => {
    try {
      const changedValues = getChangedValues(data, project);
      if (Object.keys(changedValues).length > 0) {
        const formData = new FormData();
        Object.keys(changedValues).forEach((key) => {
          const value = data[key as keyof Partial<AddProjectForm>];
          if (value !== undefined) {
            if (key === "upfrontIncentivePayment") {
              formData.append(
                key,
                `${Number(data?.upfrontIncentivePayment) * 100}`,
              );
            } else {
              // exclude comment field
              if (key !== "comment" && key !== "ProjectExtensions" && key !== "ProjectDocumentations") {
                formData.append(key, value);
              }
            }
          }
        });
        await updateProject(project?.id, formData);
        getUpdatedProjects();

        if (getFilesReq) {
          getFilesReq();
        }
      }
      handleClose();
    } catch (e: any) {
      if (e?.response?.data?.error === PROJECT_ALREADY_EXISTS_ERROR_MESSAGE) {
        setError("customerName", { message: e?.response?.data?.error });
      } else {
        handleError(e);
        console.error(e);
      }
    }
  };

  useEffect(() => {
    if (project) {
      const data = mapProjectToFormData(project)

      reset(data);
    }
  }, [project, reset]);

  useEffect(() => {
    const subscription = watch(handleFormChange);
    return () => subscription.unsubscribe();
  }, [watch, project]);

  useEffect(() => {
    const fetchBatteryModel = async (batteryModel: string) => {
      try {
        const response = await getBatteryModelReq(batteryModel);
        setValue("batteryModelId", response.data.battery?.id);
      } catch (e) {
        console.error(e);
      }
    };

    if (batteryModel && open) {
      fetchBatteryModel(batteryModel);
    }
  }, [batteryModel, open]);

  return (
    <>
      <StyledRightActionButton
        sx={{
          gap: "6px",
          whiteSpace: "nowrap",
          minWidth: "initial",
          minHeight: "initial",
          flex: 1,
          ...(!downSm && { border: 0 }),
        }}
        onClick={() => setOpen(true)}
        variant={iconOnly ? "text" : downSm ? "outlined" : "text"}
      >
        {!downSm && <SvgUserEdit />}
        {iconOnly && downSm && <SvgUserEdit />}
        {downSm && !iconOnly && "Edit project"}
      </StyledRightActionButton>
      <Modal open={open} onClose={handleClose} title={"Edit project"}>
        <FormProvider {...methods}>
          <ProjectForm
            enrollmentStatus={project?.enrolmentStatus}
            handleSubmit={handleSubmit(onSubmit)}
            handleClose={handleClose}
            isEdit={true}
            participantEnrollment={project?.participantEnrollment}
            contract={project?.contract}
            incentiveStatus={project?.incentiveStatus}
            systemSizingTool={project?.systemSizingTool}
            handleStep={handleStep}
            step={step}
          />
        </FormProvider>
      </Modal>
    </>
  );
};

export default EditProjectModal;
